import { useState } from 'react';

import { logger } from '@/lib/logger';
import { timeParts, useOrgTimeFormat } from '@/lib/time';

import type {
  GmailSendPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import EmailActionPreview from './artifacts/EmailActionPreview';

interface VersionItemProps {
  actions: ProposedAction<GmailSendPayload>[]; // Can be single or grouped
  isLatest: boolean;
  editable: boolean;
  onActionUpdate?: (action: ProposedAction<GmailSendPayload>) => void;
  onClick?: (action: ProposedAction<GmailSendPayload>) => void;
  isProcessing?: boolean;
  orgId: number;
  proposalId: number;
  /** Callback when user clicks "View Thread" - opens the thread panel */
  onOpenThread?: () => void;
  /** Number of versions for version history button */
  versionCount?: number;
  /** Callback when version history button is clicked */
  onShowVersionHistory?: () => void;
  /** When true, the proposal has exactly one action; lifts the email body 300px cap. */
  isSoleAction?: boolean;
}

export default function VersionItem({
  actions,
  isLatest,
  editable,
  onActionUpdate,
  onClick,
  isProcessing = false,
  orgId,
  proposalId,
  onOpenThread,
  versionCount,
  onShowVersionHistory,
  isSoleAction = false,
}: VersionItemProps) {
  const { timeFormat } = useOrgTimeFormat();
  const [isExpanded, setIsExpanded] = useState(false);

  const isSingleVersion = actions.length === 1;
  const action = actions[0];

  logger.debug('[VersionItem] Rendering', {
    isLatest,
    isSingleVersion,
    actionsCount: actions.length,
    actionData: {
      id: action.id,
      version_number: action.version_number,
      is_current_version: action.is_current_version,
      created_at: action.created_at,
      created_by_agent: action.created_by_agent,
      version_notes: action.version_notes,
    },
  });

  // Get attribution label
  const getAttribution = (act: ProposedAction<GmailSendPayload>) => {
    if (act.version_notes) {
      if (act.version_notes.includes('Auto-versioned')) {
        return 'Manual edit';
      }
      if (act.version_notes.toLowerCase().includes('chat')) {
        return 'Via chat';
      }
      return act.version_notes;
    }
    // Check if created by agent (would need to add this field to ProposedAction if available)
    return 'Manual edit';
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      ...timeParts(timeFormat),
    });
  };

  // Current version: full display (no label - just the editor)
  if (isLatest) {
    return (
      <div>
        {/* Full action preview - clean, no version label */}
        <EmailActionPreview
          action={action}
          proposalId={proposalId}
          editable={editable}
          onActionUpdate={onActionUpdate}
          isProcessing={isProcessing}
          orgId={orgId}
          onOpenThread={onOpenThread}
          versionCount={versionCount}
          onShowVersionHistory={onShowVersionHistory}
          isSoleAction={isSoleAction}
        />
      </div>
    );
  }

  // Old versions: chicklet display
  if (isSingleVersion) {
    // Single version chicklet
    return (
      <button
        onClick={() => onClick && onClick(action)}
        className='flex items-center gap-1.5 hover:bg-secondary rounded px-1 py-0.5 transition-colors text-left w-full'
      >
        <div className='flex items-center gap-1 px-2 py-0.5 bg-secondary border border-primary rounded-md'>
          <span className='text-xs font-medium text-secondary'>
            v{action.version_number}
          </span>
        </div>
        <span className='text-xs text-secondary'>{getAttribution(action)}</span>
        <span className='text-xs text-tertiary'>•</span>
        <span className='text-xs text-tertiary'>
          {action.created_at ? formatTime(action.created_at) : 'Unknown time'}
        </span>
      </button>
    );
  }

  // Grouped manual edits
  const firstVersion = actions[0].version_number;
  const lastVersion = actions[actions.length - 1].version_number;

  return (
    <div>
      {/* Grouped header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center gap-1.5 hover:bg-secondary rounded px-1 py-0.5 transition-colors text-left w-full'
      >
        <div className='flex items-center gap-1 px-2 py-0.5 bg-secondary border border-primary rounded-md'>
          <span className='text-xs font-medium text-secondary'>
            ({actions.length}) manual edits
          </span>
        </div>
        <span className='text-xs text-tertiary'>
          v{firstVersion} - v{lastVersion}
        </span>
        <svg
          className={`w-3 h-3 text-tertiary transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 5l7 7-7 7'
          />
        </svg>
      </button>

      {/* Expanded list */}
      {isExpanded && (
        <div className='ml-4 mt-1 space-y-1'>
          {actions.map(act => (
            <button
              key={act.id}
              onClick={() => onClick && onClick(act)}
              className='flex items-center gap-1.5 hover:bg-secondary rounded px-1 py-0.5 transition-colors text-left w-full'
            >
              <div className='flex items-center gap-1 px-1.5 py-0.5 bg-secondary border border-primary rounded'>
                <span className='text-xs font-medium text-secondary'>
                  v{act.version_number}
                </span>
              </div>
              <span className='text-xs text-tertiary'>
                {act.created_at ? formatTime(act.created_at) : 'Unknown time'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
